import React from "react";
import { motion } from "framer-motion";
import {
  Search,
  MapPin,
  Pill,
  ShoppingCart,
  PackageCheck,
  Truck,
  ShieldCheck,
  Clock3,
  FileText,
  Check,
  ArrowRight,
  HeartPulse,
  Store,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";
import { Link } from "react-router-dom";

const Pharmacy = () => {
  const pharmacyOptions = [
    {
      icon: Pill,
      title: "Medicines",
      description:
        "Find supported medicines and healthcare products through connected pharmacy services.",
    },
    {
      icon: FileText,
      title: "Prescription Medicines",
      description:
        "Access supported prescription-based medicine workflows where applicable.",
    },
    {
      icon: HeartPulse,
      title: "Health & Wellness",
      description:
        "Explore supported healthcare and wellness products from connected pharmacy services.",
    },
    {
      icon: Store,
      title: "Pharmacy Services",
      description:
        "Discover participating pharmacy services and available fulfilment options.",
    },
  ];

  const steps = [
    {
      number: "01",
      icon: Search,
      title: "Find Your Medicine",
      description:
        "Search for the medicine or healthcare product you need.",
    },
    {
      number: "02",
      icon: ShoppingCart,
      title: "Add to Cart",
      description:
        "Review available products and add the required items to your cart.",
    },
    {
      number: "03",
      icon: MapPin,
      title: "Choose Delivery",
      description:
        "Select an available delivery or pharmacy fulfilment option.",
    },
    {
      number: "04",
      icon: PackageCheck,
      title: "Track Your Order",
      description:
        "Keep track of your order through the supported pharmacy workflow.",
    },
  ];

  const benefits = [
    "Search for supported medicines and healthcare products",
    "Review medicine and product information",
    "Use prescription-based workflows where supported",
    "Choose available pharmacy fulfilment options",
    "Manage your medicine orders in one place",
    "Track supported orders through the connected experience",
  ];

  const faqs = [
    {
      question: "Can I order medicines through Doxzon?",
      answer:
        "Where pharmacy ordering is supported, patients can search for available medicines and continue through the supported ordering workflow.",
    },
    {
      question: "Can I order prescription medicines?",
      answer:
        "Prescription medicine workflows depend on the medicine, applicable prescription requirements and the pharmacy service supporting the order.",
    },
    {
      question: "Can I search for a specific medicine?",
      answer:
        "Yes. Where the pharmacy catalogue is available, patients can search for supported medicines and healthcare products.",
    },
    {
      question: "Can I get medicines delivered?",
      answer:
        "Delivery availability depends on the participating pharmacy service and the patient's location.",
    },
    {
      question: "Can I track my medicine order?",
      answer:
        "Where order tracking is supported, patients can view the relevant order and fulfilment status.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute inset-0 pointer-events-none">

            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-200/25 blur-3xl" />

            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-200/25 blur-3xl" />

            <div
              className="absolute inset-0 opacity-[0.025]"
              style={{
                backgroundImage:
                  "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />

          </div>

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Pharmacy
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Get your medicines
                <span className="block text-teal-500 mt-2">
                  with ease.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Discover supported medicines, healthcare products and pharmacy
                services through one connected Doxzon experience.
              </p>

              {/* SEARCH */}
              <div className="mt-9 rounded-2xl bg-white border border-slate-100 p-2 shadow-[0_20px_60px_rgba(15,23,42,0.10)]">

                <div className="grid md:grid-cols-[1fr_180px_auto] gap-2">

                  <div className="flex items-center gap-3 px-4 h-14 rounded-xl bg-slate-50">

                    <Search
                      size={19}
                      className="text-teal-600 shrink-0"
                    />

                    <input
                      type="text"
                      placeholder="Search medicines or products"
                      className="w-full bg-transparent outline-none text-sm placeholder:text-slate-400"
                    />

                  </div>

                  {/* <div className="flex items-center gap-3 px-4 h-14 rounded-xl bg-slate-50">

                    <MapPin
                      size={18}
                      className="text-teal-600 shrink-0"
                    />

                    <input
                      type="text"
                      placeholder="Location"
                      className="w-full bg-transparent outline-none text-sm placeholder:text-slate-400"
                    />

                  </div> */}

                 <Link to="/medicines">
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className="h-14 px-6 rounded-xl bg-slate-950 text-white inline-flex items-center justify-center gap-2 text-sm font-semibold"
  >
    Search
    <ArrowRight size={16} />
  </motion.button>
</Link>

                </div>

              </div>

              <div className="flex flex-wrap gap-2 mt-4">

                <span className="text-xs text-slate-400 mr-1">
                  Popular:
                </span>

                {[
                  "Medicines",
                  "Health Products",
                  "Wellness",
                  "Prescription",
                ].map((item) => (

                  <button
                    key={item}
                    type="button"
                    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-teal-300 hover:text-teal-600 transition"
                  >
                    {item}
                  </button>

                ))}

              </div>

              <div className="flex flex-wrap gap-6 mt-7 text-sm text-slate-500">

                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={16} className="text-teal-500" />
                  Connected pharmacy
                </span>

                <span className="inline-flex items-center gap-2">
                  <Truck size={16} className="text-teal-500" />
                  Delivery where supported
                </span>

              </div>

            </motion.div>

            {/* RIGHT VISUAL */}
            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative bg-white rounded-[30px] border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-6 md:p-7">

                <div className="flex items-center justify-between pb-5 border-b border-slate-100">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Pharmacy
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Your Medicines
                    </h3>

                  </div>

                  <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center">

                    <ShoppingCart
                      size={21}
                      className="text-teal-600"
                    />

                  </div>

                </div>

                {/* MEDICINE */}
                <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-100 p-5">

                  <div className="flex items-start gap-4">

                    <div className="w-12 h-12 rounded-xl bg-teal-100 grid place-items-center">

                      <Pill
                        size={22}
                        className="text-teal-700"
                      />

                    </div>

                    <div className="flex-1">

                      <p className="font-semibold text-sm">
                        Healthcare Product
                      </p>

                      <p className="text-xs text-slate-400 mt-1">
                        Medicine / wellness product
                      </p>

                    </div>

                    <span className="text-xs font-semibold text-teal-600">
                      Available
                    </span>

                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-5">

                    <div className="rounded-xl bg-white p-3">

                      <p className="text-[10px] text-slate-400">
                        Pharmacy
                      </p>

                      <p className="text-xs font-semibold mt-1">
                        Participating Pharmacy
                      </p>

                    </div>

                    <div className="rounded-xl bg-white p-3">

                      <p className="text-[10px] text-slate-400">
                        Fulfilment
                      </p>

                      <p className="text-xs font-semibold text-teal-600 mt-1">
                        Available
                      </p>

                    </div>

                  </div>

                </div>

                {/* DELIVERY */}
                <div className="mt-5">

                  <div className="flex items-center justify-between mb-3">

                    <p className="text-sm font-semibold">
                      Fulfilment options
                    </p>

                    <Clock3
                      size={17}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="grid grid-cols-2 gap-3">

                    <div className="rounded-xl border border-teal-500 bg-teal-50 p-4">

                      <Truck
                        size={19}
                        className="text-teal-600"
                      />

                      <p className="text-xs font-semibold mt-3">
                        Delivery
                      </p>

                      <p className="text-[10px] text-slate-400 mt-1">
                        Where supported
                      </p>

                    </div>

                    <div className="rounded-xl border border-slate-100 bg-white p-4">

                      <Store
                        size={19}
                        className="text-slate-500"
                      />

                      <p className="text-xs font-semibold mt-3">
                        Pharmacy
                      </p>

                      <p className="text-[10px] text-slate-400 mt-1">
                        Participating store
                      </p>

                    </div>

                  </div>

                </div>

                <Link
                  to="/medicines"
                  className="w-full mt-5 h-12 rounded-xl bg-slate-950 text-white font-semibold text-sm inline-flex items-center justify-center gap-2"
                >
                  View Medicines
                  <ArrowRight size={16} />
                </Link>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            PHARMACY OPTIONS
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Pharmacy Services
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Everything you need
                <span className="text-teal-500">
                  {" "}in one place.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Explore supported medicines, products and pharmacy services
                through the Doxzon healthcare journey.
              </p>

            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">

              {pharmacyOptions.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.06,
                    }}
                    whileHover={{ y: -4 }}
                    className="group rounded-xl border border-slate-100 bg-slate-50/70 p-4 hover:bg-white hover:shadow-md hover:border-teal-100 transition"
                  >
                    <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">
                      <Icon
                        size={17}
                        className="text-teal-600 group-hover:text-white transition"
                      />
                    </div>

                    <h3 className="font-sora text-sm font-semibold mt-3">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
                      {item.description}
                    </p>
                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            HOW IT WORKS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                How It Works
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Pharmacy made
                <span className="text-teal-500">
                  {" "}simple.
                </span>

              </h2>

            </div>

            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-teal-100" />

              {steps.map((item) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.number}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="relative text-center"
                  >

                    <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-slate-950 border-4 border-[#f6fbfc] shadow-lg grid place-items-center">

                      <Icon
                        size={21}
                        className="text-teal-400"
                      />

                    </div>

                    <span className="block mt-5 text-[10px] font-bold tracking-[0.2em] text-teal-600">
                      STEP {item.number}
                    </span>

                    <h3 className="font-sora text-lg font-semibold mt-2">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                      {item.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            PRESCRIPTION WORKFLOW
        ====================================================== */}
        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Prescription Support
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Connected medicine
                <span className="block text-teal-400">
                  workflows.
                </span>

              </h2>

              <p className="text-slate-400 text-lg leading-8 mt-6 max-w-xl">
                Where supported, prescription and pharmacy workflows can
                connect with other parts of the patient's healthcare journey.
              </p>

            </div>

            <div className="space-y-3">

              {[
                "Prescription information",
                "Medicine selection",
                "Pharmacy fulfilment",
                "Order information",
              ].map((item, index) => (

                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.07 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                >

                  <span className="w-9 h-9 rounded-xl bg-teal-500/10 grid place-items-center">

                    <Check
                      size={16}
                      className="text-teal-400"
                    />

                  </span>

                  <span className="text-sm text-slate-300 font-medium">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            BENEFITS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-gradient-to-br from-cyan-50 to-blue-50">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Patient Benefits
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                A simpler way to
                <span className="block text-teal-500">
                  manage medicines.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6 max-w-xl">
                Discover supported products, choose available pharmacy
                services and keep your medicine journey connected.
              </p>

            </div>

            <div className="space-y-3">

              {benefits.map((item, index) => (

                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white bg-white/80 p-4 shadow-sm"
                >

                  <span className="w-8 h-8 rounded-full bg-teal-50 text-teal-600 grid place-items-center shrink-0">

                    <Check size={15} />

                  </span>

                  <span className="text-sm md:text-base font-medium text-slate-700">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Frequently asked questions
              </h2>

            </div>

            <div className="space-y-3 mt-12">

              {faqs.map((faq, index) => (

                <motion.details
                  key={faq.question}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group rounded-2xl border border-slate-100 bg-slate-50 open:bg-white open:shadow-sm"
                >

                  <summary className="cursor-pointer list-none flex items-center justify-between gap-5 p-6 font-sora font-semibold">

                    <span>
                      {faq.question}
                    </span>

                    <span className="w-8 h-8 rounded-full bg-white group-open:bg-teal-500 group-open:text-white grid place-items-center shrink-0 transition">

                      <ArrowRight
                        size={15}
                        className="group-open:rotate-90 transition-transform"
                      />

                    </span>

                  </summary>

                  <p className="px-6 pb-6 text-slate-600 leading-7 text-sm md:text-base">
                    {faq.answer}
                  </p>

                </motion.details>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FINAL CTA
        ====================================================== */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-slate-950 text-white px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

            <div className="relative">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Pharmacy
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Find what you need,
                <span className="text-teal-400">
                  {" "}simply.
                </span>

              </h2>

              <p className="text-slate-300 max-w-2xl mx-auto mt-5 leading-7">
                Discover supported medicines and pharmacy services through
                the Doxzon healthcare experience.
              </p>

              <Link
                to="/medicines"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-4 font-semibold hover:bg-teal-50 transition"
              >
                Browse Pharmacy
                <ArrowRight size={17} />
              </Link>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default Pharmacy;